<script setup lang="ts">
import { legalCompany as c, legalHost as h } from '~/content/legal'
</script>

<template>
    <LegalPage title="Politique de confidentialité"
        description="Comment Deboosere Chauffage Sanitaire collecte, utilise et protège vos données personnelles (RGPD).">
        <p>
            La présente politique décrit la manière dont {{ c.name }} traite les données personnelles collectées via
            ce site, conformément au Règlement (UE) 2016/679 (RGPD) et à la loi n° 78-17 du 6 janvier 1978 modifiée
            (« Informatique et Libertés »).
        </p>

        <h2>1. Responsable du traitement</h2>
        <p>
            {{ c.name }}, {{ c.legalForm }}, {{ c.address }} — SIRET {{ c.siret }}.<br>
            Contact : <a :href="c.phoneHref">{{ c.phone }}</a>.
        </p>

        <h2>2. Données collectées</h2>
        <h3>Formulaire de contact</h3>
        <p>Lorsque vous nous adressez une demande, nous collectons :</p>
        <ul>
            <li>vos nom et prénom ;</li>
            <li>votre numéro de téléphone ;</li>
            <li>votre adresse e-mail (facultative) ;</li>
            <li>le type de demande et le lieu d’intervention ;</li>
            <li>le contenu de votre message ;</li>
            <li>votre acceptation d’être recontacté.</li>
        </ul>
        <p>
            Les champs obligatoires sont signalés dans le formulaire. Sans eux, nous ne pouvons pas traiter votre
            demande. Merci de ne pas communiquer de données sensibles (santé, etc.) dans le champ message.
        </p>

        <h3>Données techniques</h3>
        <p>
            Lors de votre visite, l’hébergeur enregistre des journaux techniques (adresse IP, date et heure, pages
            demandées, navigateur) nécessaires à la sécurité et au bon fonctionnement du site.
        </p>

        <h3>Clients</h3>
        <p>
            Si vous devenez client, nous traitons également les données nécessaires à l’établissement des devis, à
            la réalisation des travaux et à la facturation (identité, coordonnées, adresse du chantier, éléments de
            facturation).
        </p>

        <h2>3. Finalités et bases légales</h2>
        <ul>
            <li>
                <strong>Répondre à votre demande et établir un devis</strong> : mesures précontractuelles prises à
                votre demande (art. 6.1.b RGPD).
            </li>
            <li>
                <strong>Exécuter les prestations et les facturer</strong> : exécution du contrat (art. 6.1.b) et
                obligations légales comptables et fiscales (art. 6.1.c).
            </li>
            <li>
                <strong>Assurer la sécurité du site et prévenir le spam</strong> : intérêt légitime (art. 6.1.f).
            </li>
        </ul>
        <p>
            Vos données ne sont jamais vendues, ni utilisées à des fins de prospection commerciale sans votre accord,
            ni soumises à une prise de décision automatisée.
        </p>

        <h2>4. Destinataires</h2>
        <p>
            Vos données sont destinées exclusivement au personnel habilité de {{ c.name }}. Elles peuvent être
            transmises à nos sous-traitants techniques, dans la stricte limite de leurs missions :
        </p>
        <ul>
            <li>
                <strong>{{ h.name }}</strong> ({{ h.address }}) : hébergement du site et réception des messages du
                formulaire de contact ;
            </li>
            <li>
                <strong>Photon (komoot GmbH, Allemagne)</strong> : service de suggestion d’adresses. Le texte saisi
                dans le champ « Lieu de l’intervention » lui est transmis pour afficher des propositions ;
            </li>
            <li>
                <strong>Google (Google Ireland Ltd)</strong> : l’affichage des photos de profil des auteurs d’avis
                entraîne le chargement d’images depuis les serveurs de Google, qui reçoivent à cette occasion votre
                adresse IP.
            </li>
        </ul>
        <p>
            Elles peuvent enfin être communiquées aux autorités administratives ou judiciaires lorsque la loi
            l’exige.
        </p>

        <h2>5. Transferts hors de l’Union européenne</h2>
        <p>
            Netlify et Google sont des sociétés établies aux États-Unis. Les transferts éventuels de données vers ce
            pays sont encadrés par la décision d’adéquation de la Commission européenne relative au
            <em>EU-U.S. Data Privacy Framework</em>, auquel ces sociétés adhèrent, et/ou par les clauses
            contractuelles types adoptées par la Commission européenne.
        </p>

        <h2>6. Durées de conservation</h2>
        <ul>
            <li>Demandes de contact sans suite : 3 ans à compter du dernier contact de votre part ;</li>
            <li>Données clients : durée de la relation contractuelle, puis archivage pendant les délais de
                prescription applicables (jusqu’à 10 ans pour les travaux relevant de la garantie décennale) ;</li>
            <li>Factures et pièces comptables : 10 ans (article L123-22 du Code de commerce) ;</li>
            <li>Journaux techniques de l’hébergeur : 12 mois maximum.</li>
        </ul>

        <h2>7. Vos droits</h2>
        <p>Vous disposez, dans les conditions prévues par le RGPD, des droits suivants :</p>
        <ul>
            <li>droit d’accès, de rectification et d’effacement de vos données ;</li>
            <li>droit à la limitation du traitement et droit d’opposition ;</li>
            <li>droit à la portabilité de vos données ;</li>
            <li>droit de définir des directives relatives au sort de vos données après votre décès.</li>
        </ul>
        <p>
            Pour exercer ces droits, contactez-nous par courrier à l’adresse {{ c.address }}, ou par
            téléphone au <a :href="c.phoneHref">{{ c.phone }}</a>, en précisant l’objet de votre demande. Nous vous répondrons dans un délai d’un mois.
            En cas de doute raisonnable sur votre identité, un justificatif pourra vous être demandé.
        </p>
        <p>
            Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de la
            CNIL, 3 place de Fontenoy, TSA 80715, 75334 Paris Cedex 07 —
            <a href="https://www.cnil.fr/fr/plaintes" target="_blank" rel="noopener">www.cnil.fr/fr/plaintes</a>.
        </p>

        <h2>8. Démarchage téléphonique</h2>
        <p>
            Conformément à l’article L223-2 du Code de la consommation, si vous êtes un consommateur, vous pouvez vous
            inscrire gratuitement sur la liste d’opposition au démarchage téléphonique Bloctel :
            <a href="https://www.bloctel.gouv.fr" target="_blank" rel="noopener">www.bloctel.gouv.fr</a>.
            Votre numéro n’est utilisé que pour répondre à la demande que vous nous avez adressée.
        </p>

        <h2>9. Sécurité</h2>
        <p>
            Nous mettons en œuvre des mesures techniques et organisationnelles adaptées pour protéger vos données :
            connexion chiffrée (HTTPS), accès restreint aux seules personnes habilitées, protection anti-spam du
            formulaire.
        </p>

        <h2>10. Cookies</h2>
        <p>
            Pour tout ce qui concerne les cookies et traceurs, consultez notre
            <NuxtLink to="/cookies">politique de cookies</NuxtLink>.
        </p>

        <h2>11. Modification de la politique</h2>
        <p>
            Cette politique peut être mise à jour, notamment en cas d’évolution du site ou de la réglementation. La
            date de dernière mise à jour figure en haut de cette page.
        </p>
    </LegalPage>
</template>
